import { getXClientHeaders } from '@/utils/http/clientIdentityHeaders';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { omitOnPremOptionalMetadataHeaders } from '@/utils/http/onPremOptionalMetadataHeaders';
import { getTempUserId } from '@/utils/tempUser';
import {
  HEIDI_WORKSPACE_ID_HEADER,
  getWorkspaceId,
  getWorkspaceIdHeader,
} from '@heidi/workspace/data';

/**
 * Shared headers for raw fetch / SSE calls to ML-Scribe that bypass axios
 * interceptors (APP-8775). Call sites can pass stream-specific extras.
 *
 * Raw fetch bypasses the axios request transforms, so the on-prem optional
 * metadata omission (MOD-1488) is applied here instead — a no-op outside the
 * on-prem desktop runtime.
 */
export const buildScribeSseHeaders = (
  token: string | null | undefined,
  extra?: Record<string, string>
): Record<string, string> =>
  omitOnPremOptionalMetadataHeaders({
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: token ? `Bearer ${token}` : '',
    ...getXClientHeaders(),
    ...getWorkspaceIdHeader(),
    ...extra,
  });

const getAuthToken = () => getAuthTokenForHttpClientSync() ?? '';

/**
 * Build the same headers that the v2 axios interceptor adds, for use with
 * raw `fetch()` streaming calls that can't go through axios.
 *
 * Raw fetch callers do not get the axios 401 token-refresh retry behaviour, so
 * call sites should handle authentication failures explicitly when needed.
 */
// Transcribed verbatim from `src/api/scribeAPIV2.ts`, which re-exports it
// under its original name so no caller moved. It belongs beside
// `buildScribeSseHeaders` — the two are the same job for the two raw-transport
// shapes — and it landed HERE rather than in a new module for a measured
// reason: this file is already a recorded importer of the workspace atoms, so
// the move is a pure shrink on the workspace family's importer ratchet. A new
// file would have added an importer pair while the hub lost one, which the
// baseline comparator rejects unless git can prove the rename, and a partial
// extraction from a 3,600-line hub is exactly what it cannot prove.
export const getV2StreamHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    ...getXClientHeaders(),
  };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const workspaceId = getWorkspaceId();
  if (workspaceId) headers[HEIDI_WORKSPACE_ID_HEADER] = workspaceId;

  const tempUserId = getTempUserId();
  if (tempUserId) headers['X-Temp-User-ID'] = tempUserId;

  return omitOnPremOptionalMetadataHeaders(headers);
};
